import type { BetterFetch } from "@better-fetch/fetch";
import type { BetterFeatureClientPlugin } from "@di-devs/better-feature/client";
import type { event } from "../server/plugin";
import { createAudiencePlugin } from "./audience";
import { createEventAdminPlugin } from "./event-admin";

const adminInstanceCache = new WeakMap<
	BetterFetch,
	ReturnType<typeof createEventAdminPlugin>
>();

const audienceInstanceCache = new WeakMap<
	BetterFetch,
	ReturnType<typeof createAudiencePlugin>
>();

function getAdminInstance($fetch: BetterFetch) {
	let inst = adminInstanceCache.get($fetch);
	if (!inst) {
		inst = createEventAdminPlugin($fetch);
		adminInstanceCache.set($fetch, inst);
	}
	return inst;
}

function getAudienceInstance($fetch: BetterFetch) {
	let inst = audienceInstanceCache.get($fetch);
	if (!inst) {
		inst = createAudiencePlugin($fetch);
		audienceInstanceCache.set($fetch, inst);
	}
	return inst;
}

export const eventClient = () => {
	return {
		id: "event",
		$InferServerPlugin: {} as ReturnType<typeof event>,
		getAtoms: ($fetch) => {
			const eventAdmin = getAdminInstance($fetch);
			const audience = getAudienceInstance($fetch);

			return {
				$listEventsSignal: eventAdmin.signals.listEvents,
				$getEventSignal: eventAdmin.signals.getEvent,
				$createEventSignal: eventAdmin.signals.createEvent,
				$updateEventSignal: eventAdmin.signals.updateEvent,
				$deleteEventSignal: eventAdmin.signals.deleteEvent,
				$publishEventSignal: eventAdmin.signals.publishEvent,
				$listCategoriesSignal: eventAdmin.signals.listCategories,
				$listAudiencesSignal: audience.signals.listAudiences,
				$searchAudienceSignal: audience.signals.searchAudience,
				$createAudienceSignal: audience.signals.createAudience,
				$deleteAudienceSignal: audience.signals.deleteAudience,

				listEvents: eventAdmin.querySets.listEvents.query,
				getEvent: eventAdmin.querySets.getEvent.query,
				createEvent: eventAdmin.querySets.createEvent.query,
				updateEvent: eventAdmin.querySets.updateEvent.query,
				deleteEvent: eventAdmin.querySets.deleteEvent.query,
				publishEvent: eventAdmin.querySets.publishEvent.query,
				listCategories: eventAdmin.querySets.listCategories.query,
				listAudiences: audience.querySets.listAudiences.query,
				searchAudience: audience.querySets.searchAudience.query,
				searchAudienceMembers: audience.querySets.searchAudienceMembers.query,
				createAudience: audience.querySets.createAudience.query,
				createAudienceMember: audience.querySets.createAudienceMember.query,
				deleteAudience: audience.querySets.deleteAudience.query,

				...eventAdmin.atoms,
				...audience.atoms,
			};
		},
		getActions: ($fetch) => {
			const eventAdmin = getAdminInstance($fetch);
			const audience = getAudienceInstance($fetch);

			const allActions = {
				...eventAdmin.actions,
				...audience.actions,
			};

			return {
				event: allActions,
				...allActions,
			};
		},
		atomListeners: [
			...(() => {
				const eventAdmin = createEventAdminPlugin(
					null as unknown as BetterFetch,
				);
				const audience = createAudiencePlugin(null as unknown as BetterFetch);
				return [...eventAdmin.listeners, ...audience.listeners];
			})(),
		],
	} satisfies BetterFeatureClientPlugin;
};
